import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceDot,
} from "recharts";
import { formatCompact, formatShort } from "@/lib/format";

const Box = ({ title, subtitle, children, testid }) => (
  <div className="bg-white rounded-2xl border border-[#E8E3D9] shadow-sm p-6" data-testid={testid}>
    <div className="mb-4">
      <h3 className="font-heading text-lg font-semibold text-[#1A231F]">{title}</h3>
      {subtitle && <p className="text-sm text-[#8A9790]">{subtitle}</p>}
    </div>
    {children}
  </div>
);

export const ProjectionChart = ({ result, currency }) => {
  const data = result.projection.map((p) => ({
    age: p.age,
    corpus: Math.max(0, Math.round(p.close)),
    withdrawal: Math.round(p.withdrawal),
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div className="rounded-xl border border-[#E8E3D9] bg-white px-4 py-3 shadow-lg">
        <div className="font-heading font-bold text-[#1A231F] text-sm">Age {label}</div>
        <div className="text-xs text-[#5C6A64] mt-1">
          Corpus: <span className="font-mono font-semibold text-[#2D6A4F]">{formatCompact(d.corpus, currency)}</span>
        </div>
        <div className="text-xs text-[#5C6A64]">
          Withdrawal: <span className="font-mono font-semibold text-[#D96B43]">{formatCompact(d.withdrawal, currency)}</span>
        </div>
      </div>
    );
  };

  const depletionPoint = result.depletionAge !== null
    ? data.find((d) => d.age === result.depletionAge)
    : null;

  return (
    <Box title="Corpus Over Time" subtitle="Projected balance year by year through retirement" testid="projection-chart">
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="corpusGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2D6A4F" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#2D6A4F" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8E3D9" vertical={false} />
            <XAxis dataKey="age" tick={{ fontSize: 11, fill: "#8A9790" }} tickLine={false}
              axisLine={{ stroke: "#E8E3D9" }} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v) => formatShort(v, currency)} tick={{ fontSize: 11, fill: "#8A9790" }}
              tickLine={false} axisLine={false} width={52} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="corpus" stroke="#2D6A4F" strokeWidth={2.5}
              fill="url(#corpusGrad)" animationDuration={800} />
            {depletionPoint && (
              <>
                <ReferenceLine x={depletionPoint.age} stroke="#991B1B" strokeDasharray="4 4" />
                <ReferenceDot x={depletionPoint.age} y={0} r={5} fill="#991B1B" stroke="#fff" strokeWidth={2} />
              </>
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Box>
  );
};
